<!--
  - Copyright (c) 2022-2023. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      ref="worksRecords"
      :custom-on-row-click="gotoEdit"
      :model="model"
      :required-date-value="$parent.dateRange"
      :use-add-action="false"
      :use-custom-action="false"
      :use-delete-action="false"
      :use-edit-action="false"
      entity-name="ShiftTracking"
    >
      <template #primaryButton>
        <v-btn
          color="amber lighten-4 black--text"
          elevation="0"
          @click="showWorkStatus(-1)"
        >
          <div
            class="d-flex align-center"
          >
            <v-icon
              left
            >
              mdi-plus-circle-outline
            </v-icon>
            <template>
              <div
                class="text-truncate"
              >
                {{ $t('ShiftTrackingCard') }}
              </div>
            </template>
          </div>
        </v-btn>
        <v-btn
          color="amber lighten-4 black--text"
          elevation="0"
          @click="download"
        >
          <v-icon left>
            mdi-export
          </v-icon>
          {{ $t('export') }}
        </v-btn>
      </template>
      <template #topOnTable="{tableItems}">
        <div
          v-dragscroll
          :class="$vuetify.breakpoint.mdAndUp ? 'd-flex align-center' : ''"
          class="mb-4"
          style="width: 100%;overflow-x: scroll"
        >
          <div
            v-for="(item,index) in getRealServantInfo(tableItems)"
            :key="index"
          >
            <v-card
              class="pa-4 mr-2 d-flex flex-column"
              elevation="0"
              min-width="200px"
              outlined
              style="background: white;border-radius: 24px"
            >
              <div class="text-h4 font-weight-black mb-2">
                {{ item.name }}
              </div>
              <div class="d-flex align-center justify-center">
                <div class="text-caption">
                  {{ $t('totalTimeInHour') }}
                </div>
                <v-spacer />
                <div class="text-caption">
                  {{ item.workHour }}
                </div>
              </div>
              <div class="d-flex align-center justify-center">
                <div class="text-caption">
                  {{ $t('totalIncome') }}
                </div>
                <v-spacer />
                <div class="text-caption">
                  {{ item.totalIncome | priceDisplay }}
                </div>
              </div>
            </v-card>
          </div>
        </div>
      </template>
    </ik-data-table>
    <v-dialog
      v-model="showWorkStatusDialog"
      max-width="600px"
    >
      <v-card
        class="text-body-1 pa-6"
        elevation="0"
        style="overflow: visible;"
      >
        <div class="d-flex mb-4 align-center justify-center">
          <div class="text-h4 font-weight-bold">
            {{ $t('WorkerShiftEdit') }}
          </div>
          <v-spacer />
          <div>
            <v-btn
              icon
              @click="showWorkStatusDialog = false"
            >
              <v-icon large>
                mdi-close
              </v-icon>
            </v-btn>
          </div>
        </div>
        <div>
          <v-select
            v-model="selectedServant"
            :items="servantList"
            :menu-props="{overflowY: true, outlined: true, contentClass: 'elevation-2 ikRounded', nudgeBottom: '48px'}"
            dense
            hide-details
            item-text="name"
            item-value="id"
            outlined
          />
        </div>
        <div v-if="selectedServant">
          <v-form
            ref="form"
            v-model="valid"
            lazy-validation
          >
            <div class="d-flex mt-4 flex-column">
              <div>{{ $t('ShiftStart') }}</div>
              <div style="display: grid;grid-template-columns: repeat(2,1fr);grid-gap: 8px">
                <div>
                  <v-dialog
                    ref="dialog"
                    v-model="startDateDialog"
                    width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="startDate"
                        :rules="rules"
                        hide-details
                        outlined
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="startDate"
                      :max="today"
                      @input="startDateDialog=false"
                    />
                  </v-dialog>
                </div>
                <div>
                  <v-dialog
                    ref="dialog"
                    v-model="startTimeDialog"
                    width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="startTime"
                        :rules="rules"
                        hide-details
                        outlined
                        prepend-inner-icon="mdi-av-timer"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-time-picker
                      v-model="startTime"
                      @input="startTimeDialog=false"
                    />
                  </v-dialog>
                </div>
              </div>
            </div>
            <div class="d-flex mt-4 flex-column">
              <div>{{ $t('ShiftEnd') }}</div>
              <div style="display: grid;grid-template-columns: repeat(2,1fr);grid-gap: 8px">
                <div>
                  <v-dialog
                    ref="dialog"
                    v-model="endDateDialog"
                    width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="endDate"
                        :rules="rules"
                        hide-details
                        outlined
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-date-picker
                      v-model="endDate"
                      :max="today"
                      @input="endDateDialog=false"
                    />
                  </v-dialog>
                </div>
                <div>
                  <v-dialog
                    ref="dialog"
                    v-model="endTimeDialog"
                    width="290px"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="endTime"
                        :rules="rules"
                        hide-details
                        outlined
                        prepend-inner-icon="mdi-av-timer"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>
                    <v-time-picker
                      v-model="endTime"
                      @input="endTimeDialog=false"
                    />
                  </v-dialog>
                </div>
              </div>
            </div>
          </v-form>
          <div class="d-flex mt-4 flex-column">
            <div>{{ $t('ShiftHourlyRate') }}:</div>
            <v-spacer />
            <v-text-field
              v-model="currentHourlyWage"
              :placeholder="$t('ShiftHourlyRateInfo')"
              dense
              hide-details
              outlined
            />
          </div>
          <div class="d-flex mt-4 flex-column">
            <div>{{ $t('attention') }}:</div>
            <v-spacer />
            <v-text-field
              v-model="note"
              dense
              hide-details
              outlined
            />
          </div>
          <v-btn
            v-if="editIndex === -1"
            :disabled="!valid"
            block
            class="amber lighten-4 black--text mt-6"
            elevation="0"
            large
            @click="save"
          >
            {{ $t('save') }}
          </v-btn>
          <v-btn
            v-else
            :disabled="!valid"
            block
            class="amber lighten-4 black--text mt-6"
            elevation="0"
            large
            @click="saveEdit"
          >
            {{ $t('save') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import WorksRecords, { servantClockCorrect } from '@/model/dataLayer/api/tableAndPerson/WorksRecords'
import servant from '@/model/dataLayer/api/tableAndPerson/Servant'
import { today } from '@/model/utils/DateRepository'
import dayjs from 'dayjs'
import { groupBy } from 'lodash'
import FileSaver from 'file-saver'
import { dragscroll } from 'vue-dragscroll/src/main'

export default {
  components: { IkDataTable },
  directives: {
    dragscroll
  },
  data: function () {
    return ({
      rules: [
        v => !!v || this.$t('DateRequired')
      ],
      valid: true,
      endDateDialog: null,
      endTimeDialog: null,
      today,
      startDate: null,
      endDate: null,
      startTime: '',
      endTime: '',
      startDateDialog: null,
      startTimeDialog: null,

      editId: '',
      editIndex: '',
      currentHourlyWage: null,
      note: '',
      selectedServant: null,
      servantList: [],
      servant: null,
      showWorkStatusDialog: false,
      btnLoading: false,
      model: WorksRecords
    })
  },
  watch: {
    showWorkStatusDialog (val) {
      if (!val) {
        this.selectedServant = ''
        this.startTime = ''
        this.endTime = ''
        this.startDate = ''
        this.endDate = ''
        this.currentHourlyWage = ''
        this.note = ''
        this.editId = ''
      }
    },
    '$parent.dateRange': {
      handler: function (val) {

      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    realServantInfo () {
      return this.tableItem
    }
  },
  async mounted () {
    await this.reload()
  },
  methods: {
    async download () {
      const data = this.getRealServantInfo(this.$refs.worksRecords.items)
      let dataStrings = ['Date,Staff,Working Hours (H),Hourly Wage,Total']
      data.forEach(it => {
        const res = [this.$parent.dateRange.join('~'), it.name, it.workHour, (it.totalIncome / it.workHour).toFixed(2), it.totalIncome]
        dataStrings.push(res.join())
      })
      dataStrings = dataStrings.join('\n')
      const blob = new Blob([dataStrings], { type: 'text/plain;charset=utf-8' })
      FileSaver.saveAs(blob, 'Works Records.csv')
    },
    getRealServantInfo (item) {
      const groupInfo = groupBy(item.filter(it => it.toDateTime), 'servantName')
      const res = Object.values(groupInfo).map(arr => {
        arr.map(it => {
          it.name = it.servantName[0]
          it.totalIncome = (parseFloat(it.currentHourlyWage) * parseFloat(it.workMinute) / 60).toFixed(2)
          return it
        })
        return arr.reduce((b, c) => {
          b.workMinute = (parseFloat(b.workMinute) + parseFloat(c.workMinute)).toFixed(2)
          b.workHour = (parseFloat(b.workHour) + parseFloat(c.workHour)).toFixed(2)
          b.totalIncome = parseFloat(b.totalIncome) + parseFloat(c.totalIncome)
          return b
        }, { name: arr[0].name, workHour: 0, workMinute: 0, totalIncome: 0 })
      })
      return res
    },
    gotoEdit (item) {
      this.selectedServant = parseInt(item.servantId)
      this.editId = item.id
      const fromDateTime = dayjs(item.fromDateTime).format('YYYY-MM-DD HH:mm').split(' ')
      const toDateTime = dayjs(item.toDateTime).format('YYYY-MM-DD HH:mm').split(' ')
      this.startDate = fromDateTime[0]
      this.startTime = fromDateTime[1]
      this.endDate = toDateTime[0]
      this.endTime = toDateTime[1]
      this.note = item.note
      this.currentHourlyWage = item.currentHourlyWage
      this.showWorkStatus(1)
    },
    async saveEdit () {
      if (this.$refs.form.validate()) {
        await WorksRecords.edit({
          id: this.editId,
          fromDateTime: this.startDate + ' ' + this.startTime + ':00',
          toDateTime: this.endDate + ' ' + this.endTime + ':00',
          currentHourlyWage: this.currentHourlyWage,
          note: this.note
        })
        this.showWorkStatusDialog = false
        await this.$refs.worksRecords.reload()
      }
    },
    async save () {
      if (this.$refs.form.validate()) {
        await servantClockCorrect({
          servantId: this.selectedServant,
          fromDateTime: this.startDate + ' ' + this.startTime + ':00',
          toDateTime: this.endDate + ' ' + this.endTime + ':00',
          currentHourlyWage: this.currentHourlyWage,
          note: this.note
        })
        this.showWorkStatusDialog = false
        await this.$refs.worksRecords.reload()
      }
    },
    async reload () {
      this.servantList = await servant.getList()
    },
    showWorkStatus (item) {
      this.editIndex = item
      this.showWorkStatusDialog = true
    }
  }
}
</script>
